import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Shield, Filter, RefreshCw, ArrowLeft, ArrowRight } from "lucide-react";

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-CL", { dateStyle: "short", timeStyle: "medium" });
};

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin_directive: "Admin Directivo",
  admin_treasurer: "Admin Tesorero",
  admin: "Admin",
  coach: "Coach",
  player: "Jugador",
};

const ACTION_LABEL = {
  "role.change": "Cambio de rol",
  "user.approved": "Aprobación de usuario",
  "user.rejected": "Rechazo de usuario",
  "user.deleted": "Eliminación de usuario",
  "user.created_manual": "Alta manual de usuario",
  "charge.validated": "Validación de pago",
  "charge.rejected": "Rechazo de pago",
  "charge.deleted": "Eliminación de cobro",
  "charge.created": "Creación de cobro",
  "auth.super_login": "Login Super Admin",
  "auth.admin_login": "Login Admin",
  "integration.calendar_connected": "Conexión Google Calendar",
  "integration.drive_connected": "Conexión Google Drive",
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export default function AdminAuditLog() {
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  const [availableActions, setAvailableActions] = useState([]);
  const [filters, setFilters] = useState({ actor: "", action: "", from: "", to: "" });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filters.actor) params.actor_user_id = filters.actor.trim();
      if (filters.action) params.action = filters.action;
      if (filters.from) params.date_from = new Date(filters.from).toISOString();
      if (filters.to) {
        // End-of-day: include full "to" date
        const d = new Date(filters.to);
        d.setHours(23, 59, 59, 999);
        params.date_to = d.toISOString();
      }
      const r = await api.get("/admin/audit_log", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar la auditoría");
    } finally {
      setLoading(false);
    }
  };

  const loadActions = async () => {
    try {
      const r = await api.get("/admin/audit_log/actions");
      setAvailableActions(r.data || []);
    } catch { /* silently ignore */ }
  };

  useEffect(() => { loadActions(); }, []);
  useEffect(() => { load(); }, [page, pageSize]);

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else load();
  };

  const clearFilters = () => {
    setFilters({ actor: "", action: "", from: "", to: "" });
    setPage(1);
    setTimeout(load, 0);
  };

  // Guard: solo super_admin
  if (user && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8" data-testid="audit-log-page">
        <div className="mb-6">
          <div className="overline text-cyan-400 flex items-center gap-2">
            <Shield size={13}/> Administración · Auditoría
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-none mt-2">
            REGISTRO DE ACTIVIDAD
          </h1>
          <p className="text-[#A1A1AA] mt-2 text-sm max-w-2xl">
            Log append-only de acciones sensibles: cambios de rol, aprobaciones, movimientos financieros y accesos admin. Solo lectura.
          </p>
        </div>

        {/* Filtros */}
        <div className="tactical-card p-4 mb-4" data-testid="audit-filters">
          <div className="overline text-[#71717A] mb-3 flex items-center gap-1.5">
            <Filter size={11}/> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">Usuario (user_id)</span>
              <input
                className="input-tactical mt-1"
                placeholder="user_..."
                value={filters.actor}
                onChange={e => setFilters(f => ({ ...f, actor: e.target.value }))}
                data-testid="audit-filter-actor"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">Acción</span>
              <select
                className="input-tactical mt-1"
                value={filters.action}
                onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
                data-testid="audit-filter-action"
              >
                <option value="">Todas</option>
                {availableActions.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">Desde</span>
              <input
                type="date"
                className="input-tactical mt-1"
                value={filters.from}
                onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
                data-testid="audit-filter-from"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">Hasta</span>
              <input
                type="date"
                className="input-tactical mt-1"
                value={filters.to}
                onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
                data-testid="audit-filter-to"
              />
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={applyFilters} className="btn-primary text-xs" data-testid="audit-apply">
              Aplicar
            </button>
            <button onClick={clearFilters} className="btn-ghost text-xs" data-testid="audit-clear">
              Limpiar
            </button>
            <button onClick={load} className="btn-ghost text-xs flex items-center gap-1 ml-auto" data-testid="audit-refresh">
              <RefreshCw size={12}/> Actualizar
            </button>
          </div>
        </div>

        {/* Tabla */}
        <div className="tactical-card overflow-x-auto" data-testid="audit-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#27272A] text-left text-[#A1A1AA]">
                <th className="p-3 overline">Fecha / Hora</th>
                <th className="p-3 overline">Usuario</th>
                <th className="p-3 overline">Rol</th>
                <th className="p-3 overline">Acción</th>
                <th className="p-3 overline">Recurso</th>
                <th className="p-3 overline">Detalle</th>
                <th className="p-3 overline">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-10 text-center text-[#71717A]">Cargando…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-[#71717A]">No hay registros para los filtros aplicados.</td></tr>
              )}
              {!loading && items.map(row => (
                <tr key={row.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`audit-row-${row.id}`}>
                  <td className="p-3 font-mono text-[11px] text-white whitespace-nowrap">{fmtDateTime(row.timestamp)}</td>
                  <td className="p-3">
                    <div className="text-white">{row.actor_name || "—"}</div>
                    <div className="text-[10px] text-[#71717A]">{row.actor_email || row.actor_user_id}</div>
                  </td>
                  <td className="p-3">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#27272A] text-[#A1A1AA]">
                      {ROLE_LABEL[row.actor_role] || row.actor_role || "—"}
                    </span>
                  </td>
                  <td className="p-3 text-white">{ACTION_LABEL[row.action] || row.action}</td>
                  <td className="p-3 text-[#A1A1AA]">
                    <div>{row.resource_type}</div>
                    <div className="text-[10px] font-mono text-[#71717A] truncate max-w-[220px]" title={row.resource_id}>{row.resource_id || "—"}</div>
                  </td>
                  <td className="p-3 max-w-[380px]">
                    <DetailCell detail={row.detail}/>
                  </td>
                  <td className="p-3 text-[10px] font-mono text-[#71717A]">{row.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {total > 0 && (
          <div className="flex flex-wrap items-center gap-3 mt-3 px-1" data-testid="audit-pagination">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#71717A]">
              <span>Mostrar</span>
              <select
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="input-tactical w-auto py-1 text-xs"
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>por página</span>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">
              Mostrando <strong className="text-white">{start}</strong> a <strong className="text-white">{end}</strong> de <strong className="text-white">{total}</strong>
            </span>
            <div className="flex items-center gap-1 ml-auto">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-ghost text-[10px] uppercase tracking-widest border border-[#27272A] px-3 py-1 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                data-testid="audit-prev"
              >
                <ArrowLeft size={11}/> Anterior
              </button>
              <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] px-2">
                Página <strong className="text-white">{page}</strong> de <strong className="text-white">{totalPages}</strong>
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-ghost text-[10px] uppercase tracking-widest border border-[#27272A] px-3 py-1 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                data-testid="audit-next"
              >
                Siguiente <ArrowRight size={11}/>
              </button>
            </div>
          </div>
        )}
        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

function DetailCell({ detail }) {
  if (!detail || (typeof detail === "object" && Object.keys(detail).length === 0)) {
    return <span className="text-[#52525B] text-xs italic">Sin detalle</span>;
  }
  const entries = Object.entries(detail).filter(([, v]) => v !== null && v !== undefined && v !== "");
  return (
    <div className="space-y-0.5">
      {entries.map(([k, v]) => (
        <div key={k} className="text-[11px] flex gap-1.5">
          <span className="text-[#71717A] uppercase tracking-wider">{k}:</span>
          <span className="text-white break-words">
            {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}
